import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAnimatronicBySlug } from '../hooks/useAnimatronicBySlug';
import AnimatronicInfo from '../components/animatronics/AnimatronicInfo';
import MediaGallery from '../components/media/gallery/MediaGallery';
import SupplierCard from '../components/suppliers/SupplierCard';
import AnimatronicLocationDetails from '../components/animatronics/AnimatronicLocationDetails';
import AnimatronicDiscussion from '../components/animatronics/AnimatronicDiscussion';
import { processMediaItems } from '../utils/mediaProcessor';
import LoadingState from '../components/ui/LoadingState';
import { supabase } from '../lib/supabase';

const AnimatronicDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { animatronic, isLoading, error } = useAnimatronicBySlug(id);
  const [topicData, setTopicData] = React.useState<{ id: string; comment_count: number } | null>(null);
  const processedMedia = animatronic ? processMediaItems(animatronic.media) : [];

  React.useEffect(() => {
    const fetchTopic = async () => {
      if (!id) return;
      
      const { data } = await supabase
        .from('forum_topics')
        .select('id, comments:forum_comments(count)')
        .eq('animatronic_id', id)
        .single();

      if (data) {
        setTopicData({
          id: data.id,
          comment_count: data.comments?.[0]?.count || 0
        });
      }
    };

    fetchTopic();
  }, [id]);

  if (isLoading) {
    return <LoadingState message="Loading animatronic details..." />;
  }

  if (error || !animatronic) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-content-primary">Animatronic not found</h1>
          <button
            onClick={() => navigate('/database')}
            className="mt-4 inline-flex items-center text-brand hover:text-brand-light"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Database
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/database')}
        className="inline-flex items-center text-brand hover:text-brand-light mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Database
      </button>

      {/* Animatronic Info */}
      <AnimatronicInfo animatronic={animatronic} />

      {/* Media Gallery */}
      {processedMedia.length > 0 && (
        <div className="mb-8">
          <MediaGallery media={processedMedia} name={animatronic.name} />
        </div>
      )}

      {/* Supplier Info */}
      {animatronic.supplier && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-content-primary mb-4">
            Sponsored By
          </h2>
          <SupplierCard supplier={animatronic.supplier} />
        </div>
      )}

      {/* Description */}
      <div className="card p-6 mb-8 prose prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {animatronic.description}
        </ReactMarkdown>
      </div>

      {/* Location Details */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-content-primary mb-4">
          Location Details
        </h2>
        <AnimatronicLocationDetails
          location={{
            attraction: animatronic.attraction,
            park: animatronic.park,
            latitude: animatronic.gps_coordinates.split(',')[0],
            longitude: animatronic.gps_coordinates.split(',')[1]
          }}
        />
      </div>

      {/* Discussion Link */}
      {topicData && (
        <AnimatronicDiscussion
          animatronicId={animatronic.id}
          topicId={topicData.id}
          commentCount={topicData.comment_count}
        />
      )}
    </div>
  );
};

export default AnimatronicDetailPage;